import { cn } from "@/utils/theme";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Icon,
  Input,
  inputVariants,
  ScrollArea,
  buttonVariants,
} from "@elaniintc/goes-design-system";
import { DropdownListItem, type IDropdownListItem } from "./dropdown-list-item";
import { DropdownListSeparator } from "./dropdown-list-separator";
import type { ComponentProps } from "react";
import { useState } from "react";
import type { VariantProps } from "class-variance-authority";

type ActionVariant =
  | "info"
  | "warning"
  | "disabled"
  | "action"
  | "success"
  | "danger"
  | "neutral"
  | null;

interface DropdownListGroup {
  groupId: string;
  groupLabels: string[];
  items: IDropdownListItem[];
  icon?: ComponentProps<typeof Icon> & {
    variant?: ActionVariant;
  };
  type?: string;
}

type DropdownListOptions = IDropdownListItem[] | DropdownListGroup[];
type ButtonVariant = VariantProps<typeof buttonVariants>["variant"];

/**
 * Type guard to check if options are grouped
 *
 * @param options - Array of dropdown items or groups.
 * @returns
 */
const isGroupedOptions = (
  options: DropdownListOptions,
): options is DropdownListGroup[] => {
  return options.length > 0 && "groupId" in options[0];
};

interface DropdownListProps {
  options: DropdownListOptions;
  group?:
    | "middle"
    | "up"
    | "up-left"
    | "up-right"
    | "left"
    | "right"
    | "down"
    | "down-left"
    | "down-right"
    | "middle-left"
    | "middle-right";
  iconPosition?: "left" | "right" | "hidden";
  badgePosition?: "right" | "hidden";
  widthClassName?: string; // css classname or tailwind style e.g., "w-full", "w-64"
  heightClassName?: string; // css classname or tailwind style e.g., "h-64", "h-full"
  scrollClassName?: string; // css classname or tailwind style e.g., "h-64", "h-full"
  disabled?: boolean;
  search?: {
    placeholder: string;
    query: string;
    onSearch: (query: string) => void;
  };
  fetchMore?: {
    hasMore: boolean;
    isFetching: boolean;
    fetchNextPage: () => void;
  };
  placeholder?: string;
  placeholderIcon?: ComponentProps<typeof Icon>;
  selectedValue?: IDropdownListItem;
  selectedValues?: IDropdownListItem[];
  multiSelect?: boolean;
  selectedGroup?: DropdownListGroup;
  buttonVariant?: ButtonVariant;
  toggleSelection?: (
    value: IDropdownListItem,
    group?: DropdownListGroup,
  ) => void;
  labelClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  isLoading?: boolean;
  id?: string;
  /** Layout for grouped headers. Defaults to "breadcrumb". */
  separatorVariant?: "breadcrumb" | "stacked";
}

const DropdownList = ({
  isLoading = false,
  options,
  iconPosition = "left",
  badgePosition = "right",
  search,
  fetchMore,
  widthClassName,
  heightClassName,
  scrollClassName,
  placeholder,
  placeholderIcon,
  toggleSelection,
  selectedValue,
  selectedValues,
  multiSelect = false,
  selectedGroup,
  buttonVariant,
  group,
  disabled,
  labelClassName = "",
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  id,
  separatorVariant = "breadcrumb",
}: DropdownListProps) => {
  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen ?? internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  const isItemSelected = (item: IDropdownListItem): boolean => {
    if (multiSelect && selectedValues) {
      return selectedValues.some((v) => v.value === item.value);
    }
    return selectedValue?.value === item.value;
  };

  const multiSelectLabel =
    multiSelect && selectedValues && selectedValues.length > 0
      ? selectedValues
          .map((v) => (typeof v.label === "string" ? v.label : v.value))
          .join(", ")
      : null;

  const handleItemClick = (
    item: IDropdownListItem,
    group?: DropdownListGroup,
  ) => {
    if (toggleSelection) {
      toggleSelection(item, group);
    }
    if (!multiSelect) {
      setOpen(false);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          id={id}
          disabled={disabled}
          group={group}
          variant={buttonVariant || "tertiary"}
          className={cn(
            "flex justify-start items-center group min-w-0 relative gap-2",
            inputVariants({
              className: cn(
                "bg-neutral-object hover:bg-neutral-object-accent-silent data-[state=open]:bg-neutral-object-accent-quiet data-[state=open]:border-neutral-loud",
                widthClassName,
              ),
              group,
            }),
          )}
        >
          {(isLoading || fetchMore?.isFetching) && (
            <Icon
              name="spinner-third"
              type="fas"
              className="animate-spin text-body-quiet text-sm"
            />
          )}
          {selectedGroup?.icon && (
            <Badge
              shape="pill"
              size="sm"
              variant={selectedGroup.icon.variant || "action"}
              className={cn(
                "min-w-6 px-1 flex justify-center items-center text-body",
              )}
            >
              <Icon {...selectedGroup.icon} />
            </Badge>
          )}
          <RenderIcon selectedValue={selectedValue} />
          {!selectedValue && placeholderIcon && (
            <Icon {...placeholderIcon} className="text-body" />
          )}
          <p
            className={cn(
              "truncate flex-1 min-w-0 font-normal pr-6 text-start",
              selectedValue?.label || multiSelectLabel
                ? "text-body"
                : "text-body-quiet",
              labelClassName,
            )}
          >
            {multiSelectLabel ?? selectedValue?.label ?? placeholder}
          </p>
          {selectedValue?.badge && (
            <Badge
              size="sm"
              shape={"pill"}
              {...selectedValue.badge}
              variant={selectedValue.badge.variant ?? "neutral"}
              className={cn(
                "shrink-0 ml-auto mr-6",
                selectedValue.badge.className,
              )}
            ></Badge>
          )}
          <Icon
            type="far"
            name="angle-down"
            className="group-data-[state=open]:rotate-180 transition-transform duration-200 group-data-[state=open]:text-body absolute right-3 top-1/2 transform -translate-y-1/2"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          "flex flex-col p-0 rounded-lg border-[0.5px] border-neutral bg-neutral-object text-body-loud font-semibold shadow-neutral-xl overflow-hidden",
          heightClassName,
        )}
        style={{
          width:
            "var(--radix-popper-anchor-width, var(--radix-dropdown-menu-trigger-width))",
        }}
        side="bottom"
        align="start"
      >
        {search && (
          <div className="border-b border-neutral-quiet p-3 relative ">
            <Icon
              type="far"
              name="search"
              className="absolute left-5 top-1/2 transform -translate-y-1/2 text-body"
            />
            <Input
              variant="sm"
              className="text-sm w-full font-normal flex-auto pl-9"
              placeholder={search.placeholder || "Buscar..."}
              onChange={(e) => search.onSearch(e.target.value)}
              value={search.query}
            />
          </div>
        )}
        <ScrollArea
          className={cn("p-2", scrollClassName, "max-h-60 overflow-auto")}
        >
          {/* w-0 min-w-full: Radix's ScrollArea wraps children in a display:table div that
              sizes to content, which breaks descendant truncate/min-w-0. This forces it back
              to the viewport width so long labels ellipsis instead of pushing badges offscreen. */}
          <div className="w-0 min-w-full">
            {(() => {
              const toggle = (
                item: IDropdownListItem,
                group?: DropdownListGroup,
              ) => {
                return () => handleItemClick(item, group);
              };
              if (isGroupedOptions(options)) {
                // Render grouped options
                return options.map((group) => {
                  return (
                    <div
                      key={group.groupId}
                      className="flex flex-col gap-2 mb-2"
                    >
                      <DropdownListSeparator
                        options={group.groupLabels}
                        icon={group.icon}
                        variant={separatorVariant}
                      />
                      {group.items.map((item) => {
                        return (
                          <DropdownListItem
                            key={item.value}
                            iconPosition={iconPosition}
                            isSelected={
                              multiSelect
                                ? isItemSelected(item)
                                : selectedValue?.value === item.value &&
                                  selectedGroup?.groupId === group.groupId
                            }
                            showCheckbox={multiSelect}
                            badgePosition={badgePosition}
                            {...item}
                            toggleSelection={toggle(item, group)}
                          />
                        );
                      })}
                    </div>
                  );
                });
              }
              if (options.length > 0) {
                // Render simple item list
                return options.map((item) => (
                  <DropdownListItem
                    key={item.value}
                    iconPosition={iconPosition}
                    badgePosition={badgePosition}
                    isSelected={
                      multiSelect
                        ? isItemSelected(item)
                        : selectedValue?.value === item.value
                    }
                    showCheckbox={multiSelect}
                    {...item}
                    toggleSelection={(item) => handleItemClick(item)}
                  />
                ));
              }
              return (
                <div className="flex items-center justify-center py-5">
                  <span className="text-body-quiet">
                    No se encontraron coincidencias
                  </span>
                </div>
              );
            })()}
            {fetchMore && fetchMore.hasMore && (
              <div className="w-full flex items-center justify-start">
                <Button
                  variant="ghost-blue"
                  size="xs"
                  type="button"
                  disabled={fetchMore.isFetching}
                  className="shadow-none"
                  onClick={() => {
                    if (fetchMore.isFetching) return;
                    fetchMore.fetchNextPage();
                  }}
                >
                  Más resultados
                  <Icon
                    type="far"
                    name={fetchMore.isFetching ? "spinner-third" : "plus"}
                    className={cn(
                      "text-current",
                      fetchMore.isFetching && "animate-spin",
                    )}
                  />
                </Button>
              </div>
            )}
          </div>
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { DropdownList };
export type { IDropdownListItem, DropdownListGroup, DropdownListOptions };

const RenderIcon = ({
  selectedValue,
}: {
  selectedValue?: IDropdownListItem;
}) => {
  if (selectedValue?.icon) {
    return <Icon {...selectedValue.icon} className="text-body" />;
  } else if (selectedValue?.iconNode) {
    return <>{selectedValue.iconNode}</>;
  }
  return null;
};
